import {
  Button,
  classSet,
  EllipsisMenu,
  Pagination,
  SearchInput,
  useFilteredPagination,
  useRect,
  useWindowSize
} from '@platform/ui-library';
import {
  CSSProperties,
  FC,
  useState
} from 'react';
import { applicationUi } from '@app/const';
import { useCustomNavigate } from '../../hooks';
import { Design } from '../../types';
import { createIntentCsvConfig, downloadFile } from '../../utilities';
import { Table } from '../table';
import styles from '../modals/IntentDetails.module.scss';

interface ListIntentProps {
  serialized: Design;
}

export const ListIntents: FC<ListIntentProps> = ({
  serialized
}) => {
  const [designIntentTableSearch, setDesignIntentTableSearch] = useState<string>('');
  const navigate = useCustomNavigate();
  const [ref, refRect] = useRect();
  const [_, windowHeight] = useWindowSize();

  const containerStyle: CSSProperties = {
    maxHeight: (windowHeight - (refRect?.top ?? 0) - 20) + 'px'
  };

  const allTableData = serialized.components.intents.map((designIntent) => {
    const { id, name } = designIntent;
    const { localeData, commonIntent } = designIntent.props.config;
    return {
      id: id,
      name,
      description: designIntent.props.config.description ?? '',
      utterances: localeData['en-US']?.phrases.length ?? 0,
      library: commonIntent?.name || 'None',
      '': {
        content: (
          <Button
            key={id}
            icon={'edit'}
            type='button'
            theme={['outline', 'small', 'round']}
            onClick={() => {
              navigate(`edit/${id}`);
            }}
          ></Button>
        ),
        raw: ''
      }
    };
  });

  const {
    filteredItemsCount,
    hasMultiplePages,
    items: paginatedTableData,
    onSort,
    page,
    pageSize,
    setPage,
    setSearchString,
    sortAsc,
    sortColumn
  } = useFilteredPagination(allTableData, ['name'], 1, applicationUi.itemsPerPage);

  return (
    <div ref={ref} className={styles.listIntentsContainer} style={containerStyle}>
      <div>
        <div className={classSet([styles.header, styles.listHeaderFlex])}>
          <h2 className={classSet([styles.title, styles.withIcon])}>
            Intents
          </h2>
          <div className={classSet([styles.listMenusFlex])}>
            <SearchInput
              name={'searchIntents'}
              placeholder={'Search Intents'}
              onChange={(inputValue) => {
                setDesignIntentTableSearch(inputValue);
                setSearchString(inputValue);
              }}
              extraClasses={styles.input}
              value={designIntentTableSearch}
            />
            <Button
              icon='download'
              onClick={() => {
                downloadFile(createIntentCsvConfig({ serialized }));
              }}
              theme='outline'
            />
            <EllipsisMenu
              items={[
                {
                  action: () => {
                    navigate('import');
                  },
                  label: 'Import from Library'
                },
                {
                  action: () => {
                    navigate('create');
                  },
                  label: 'Create Custom Intent'
                },
                {
                  action: () => {
                    navigate('import-from-csv');
                  },
                  label: 'Import from CSV'
                }
              ]}
            >
              <Button icon="plus" theme="outlineHighlight" type="button">
                Add Intent
              </Button>
            </EllipsisMenu>
          </div>
        </div>
        <div className="content flex-shrink">
          <Table
            className={styles.fullListTable}
            data={paginatedTableData}
            sortByColumns={['name']}
            sortColumn={sortColumn}
            sortAsc={sortAsc}
            onSort={onSort}
          />
          {hasMultiplePages && (
            <Pagination
              totalItems={filteredItemsCount}
              itemsPerPage={pageSize}
              initialPage={page}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
